import { Grid, Stack, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { formatPrice, getPriceSuggestionType } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { PriceSuggestionPriority, PriceSuggestionType } from '@retail/products/types';
import { EditFieldButton } from '../../EditFieldButton';

interface Props {
  priceSuggestionPriority: PriceSuggestionPriority[];
  activePriceSuggestionType?: PriceSuggestionType;
  priceSuggestionPrice?: number;
  markupPrice?: number;
  markupCoverageDegree?: number;
  onEdit?: () => void;
}

export function SalesPriceAccordionSummary({
  priceSuggestionPriority,
  activePriceSuggestionType,
  priceSuggestionPrice,
  markupPrice,
  markupCoverageDegree,
  onEdit,
}: Props) {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const activeMarkup = !activePriceSuggestionType || activePriceSuggestionType === 'MARKUP';

  const price = activeMarkup ? markupPrice : priceSuggestionPrice;

  const priority =
    priceSuggestionPriority.find(
      (prio) =>
        prio.priceSuggestionType === activePriceSuggestionType ||
        prio.priceSuggestionType === 'MARKUP'
    )?.priority ?? 1;

  return (
    <Grid gap={2} container sx={{ flexWrap: 'nowrap', flexGrow: 1 }}>
      <Grid size={3} alignSelf="center">
        <Typography variant="body2">{t('myAssortment.productCard.salesPriceCondition')}</Typography>
      </Grid>
      <Grid size={7.6}>
        <Grid container>
          <Grid size={8} p={1}>
            <Stack flexDirection="row" gap={0.5}>
              <Typography variant="subtitle2" color="textSecondary">
                {priority}.
              </Typography>

              {activeMarkup ? (
                <Stack flexDirection="row" gap={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('myAssortment.columns.salesCoverage')}:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {markupCoverageDegree}%
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {getPriceSuggestionType(t, activePriceSuggestionType)}
                </Typography>
              )}
            </Stack>
          </Grid>
          <Grid size={3.4} p={1}>
            {
              <Typography variant="body2">
                {t('myAssortment.productCard.price', {
                  price: price ? formatPrice(price, i18n.language) : '-',
                })}
              </Typography>
            }
          </Grid>
        </Grid>
      </Grid>

      {onEdit && (
        <Grid alignSelf="center">
          <EditFieldButton onEdit={onEdit} />
        </Grid>
      )}
    </Grid>
  );
}
